import { inject } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  CanActivateFn,
  CanDeactivateFn,
  PRIMARY_OUTLET,
  Router,
  Routes,
  UrlTree,
} from '@angular/router';
import { AppAuthService, type PermissionTuple } from '@trueform/ui/core';

import { DrawerFormVeto } from './drawer-form-veto.service';

/**
 * One drawer URL a generated child-route set must support
 * (`DESIGN-WUIAM-135` §2.2 / §3.1).
 */
export interface DrawerRouteDef {
  /**
   * The child path segment(s): the literal `'new'`, the bare `':id'`, or a
   * named secondary written as `':id/<name>'` (e.g. `':id/history'`).
   */
  readonly path: string;
  /** The action id the screen's dispatch effect reads from `data['drawer']`. */
  readonly drawer: string;
  /**
   * The ACTION permission tuple, derived from the owning config (never
   * re-declared); `null` = viewer-reachable (history-style drawers).
   */
  readonly permission: PermissionTuple | null;
  /** Form-bearing drawers get the dirty veto (`drawerDirtyGuard`). */
  readonly veto: boolean;
}

/** The drawer the section's `firstChild` route currently names, if any. */
export interface ActiveDrawer {
  readonly drawer: string;
  /** The `:id` param of the active drawer; `null` for the record-less `new`. */
  readonly id: string | null;
}

/**
 * Emits the component-less, terminal child records that make a drawer a real
 * URL (`<list>/new`, `<list>/:id`, `<list>/:id/<named>`).
 *
 * Ordering is load-bearing and generated here, never left to the caller:
 * the literal `new` record comes FIRST so `/currencies/new` always means
 * "create"; the bare `:id` record comes LAST so a two-segment secondary
 * (`:id/history`) is never shadowed. CONSEQUENCE, accepted by design: a record
 * whose id is literally `new` is edit-unaddressable — every real id is
 * server-generated (DCS UUIDs, PHS uuid/numeric), and the MSW seed stores
 * enforce the invariant (mock-enforces-the-contract rule).
 *
 * Every record is `children: []` — the NG04014 terminal shape the existing
 * company-less canonicalize literals already ship — so no `<router-outlet>` is
 * needed: a matched child renders nothing and the section's list component
 * stays mounted (no chunk, no refetch, no lost scroll position).
 *
 * `opts.passthrough` emits the same paths with NO guards and NO data: those are
 * the company-less twins declared under the canonicalize literals, so a pasted
 * `/catalogs/currencies/123` MATCHES (instead of falling to `**`), the
 * canonicalize guard fires, and `buildCompanyScopedUrl` carries the trailing
 * segments through verbatim.
 */
export function createDrawerChildRoutes(
  defs: readonly DrawerRouteDef[],
  opts: { readonly passthrough?: boolean } = {},
): Routes {
  return [...defs]
    .sort((a, b) => matchRank(a.path) - matchRank(b.path))
    .map((def) =>
      opts.passthrough
        ? { path: def.path, children: [] }
        : {
            path: def.path,
            data: { drawer: def.drawer },
            canActivate: [drawerActionGuard(def.permission)],
            ...(def.veto ? { canDeactivate: [drawerDirtyGuard] } : {}),
            children: [],
          },
    );
}

/** `new` first, named secondaries in between, the bare `:id` last (stable sort). */
function matchRank(path: string): number {
  if (path === 'new') {
    return 0;
  }
  return path === ':id' ? 2 : 1;
}

/**
 * The section key used to pair a drawer's route guard with the form attached on
 * that section: the route's OWN url segments joined. Callers pass the section
 * route (the guard its `parent`, the bridge its own `ActivatedRoute`), so both
 * derive the SAME string with no configuration to drift (`DESIGN-WUIAM-135`
 * §3.5).
 */
export function sectionKey(route: ActivatedRouteSnapshot | null | undefined): string {
  return (route?.url ?? []).map((segment) => segment.path).join('/');
}

/**
 * Reads the drawer state off a section route: its FIRST CHILD is the
 * component-less drawer record (nothing renders for it), and its
 * `data['drawer']` + `:id` param are the state. `null` = URL is the plain list.
 * Accepts an `ActivatedRoute` or a snapshot so the bridge and specs share one
 * implementation.
 */
export function activeDrawer(
  route: ActivatedRoute | ActivatedRouteSnapshot | null | undefined,
): ActiveDrawer | null {
  if (route === null || route === undefined) {
    return null;
  }
  // The route's own SNAPSHOT already owns the whole snapshot tree during
  // activation, while an ActivatedRoute's child route may not have its
  // `snapshot` field assigned yet (the component that provides the bridge is
  // constructed before the child's field is set) — read the snapshot tree, not
  // `firstChild.snapshot`.
  const section: ActivatedRouteSnapshot | undefined = 'snapshot' in route ? route.snapshot : route;
  const child = section?.firstChild ?? null;
  if (child === null) {
    return null;
  }
  const drawer = child.data['drawer'];
  if (typeof drawer !== 'string') {
    return null;
  }
  return { drawer, id: child.paramMap.get('id') };
}

/**
 * The list URL for a drawer URL: `url` minus the drawer path's segments, query
 * string and fragment preserved. Used by `drawerActionGuard` to land a denied
 * deep link SILENTLY on the list — no toast, no error flash, no unauthorized
 * fallback (that library redirect is for VIEW permissions; the section's own
 * view/auth guards are untouched).
 */
export function trimDrawerUrlTree(
  router: Router,
  url: string,
  drawerPath: string | null | undefined,
): UrlTree {
  const tree = router.parseUrl(url);
  const primary = tree.root.children[PRIMARY_OUTLET];
  const segmentsToTrim = Math.max(
    (drawerPath ?? '').split('/').filter((segment) => segment.length > 0).length,
    1,
  );
  if (primary === undefined || primary.segments.length <= segmentsToTrim) {
    return tree;
  }
  return router.createUrlTree(
    primary.segments.slice(0, -segmentsToTrim).map((segment) => segment.path),
    {
      queryParams: tree.queryParams,
      fragment: tree.fragment ?? undefined,
    },
  );
}

/**
 * The silent ACTION guard every generated drawer record carries.
 *
 * Module-private on purpose: the generator below is the only caller (a
 * hand-written record uses `createDrawerChildRoutes` too), and knip treats an
 * export nobody imports as dead code.
 *
 * Auth and the section's VIEW permission are already enforced by the parent
 * record's guard chain; this checks only the action permission, with the same
 * `AppAuthService` the screens use. `null` = viewer-reachable. Denial returns a
 * trimmed list `UrlTree` (no flash, no toast).
 */
function drawerActionGuard(permission: PermissionTuple | null): CanActivateFn {
  return (route, state) => {
    if (permission === null) {
      return true;
    }
    if (inject(AppAuthService).hasPermission(permission)) {
      return true;
    }
    return trimDrawerUrlTree(inject(Router), state.url, route.routeConfig?.path);
  };
}

/**
 * The shared dirty-form veto for every veto-bearing drawer record.
 *
 * Looks the attached form up in the root {@link DrawerFormVeto} registry by the
 * PARENT (section) route's key: clean or absent → allow, dirty → ask. The
 * sheet's own close surfaces (Esc / X / Cancel / mask) go through the bridge's
 * `zBeforeClose` hook instead; both consult the same registry entry, so a
 * discard is confirmed once per attempt, whichever surface started it.
 */
export const drawerDirtyGuard: CanDeactivateFn<unknown> = (_component, route) => {
  const veto = inject(DrawerFormVeto);
  const key = sectionKey(route.parent ?? route);
  return !veto.isDirty(key) || veto.confirmDiscard(key);
};
